"use client";

import { useState } from "react";
import Link from "next/link";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import StickyContactButtons from "@/components/common/StickyContactButtons";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface ImagePopupProps {
  src: string;
  alt: string;
  onClose: () => void;
}

const ImagePopup = ({ src, alt, onClose }: ImagePopupProps) => (
  <div
    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all hover:scale-110"
      aria-label="Close popup"
    >
      <Icon name="XMarkIcon" size={32} />
    </button>
    <div className="relative max-w-6xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
      <AppImage src={src} alt={alt} className="w-full h-full object-contain rounded-lg" />
    </div>
  </div>
);

export default function SwastikApartmentPage() {
  const [popupImage, setPopupImage] = useState<{ src: string; alt: string } | null>(null);

  const property = {
    title: "Swastik Apartment",
    location: "NH-12 Diamond Harbour Near Pailan Market, Chak Rajmolla, West Bengal 700104",
    type: "Residential Apartment",
    status: "New Launch",
    description:
      "Introducing Swastik Apartment — a premium residential project located on NH-12 Diamond Harbour Road near Pailan Market, Chak Rajmolla. Enjoy modern living with excellent connectivity, close proximity to markets, schools, and essential amenities in a rapidly developing locality of West Bengal.",
    images: [
      {
        id: "img_1",
        src: "/assets/images/cover-1773997396852.jpg",
        alt: "Swastik Apartment building exterior view at NH-12 Diamond Harbour near Pailan Market, Chak Rajmolla, West Bengal",
      },
      {
        id: "img_2",
        src: "/assets/images/1-1773999995664.jpg",
        alt: "Swastik Apartment interior or site view at NH-12 Diamond Harbour near Pailan Market, Chak Rajmolla, West Bengal",
      },
      {
        id: "img_3",
        src: "/assets/images/Buildup_Area-1774000869825.png",
        alt: "Swastik Apartment flat built-up area plan showing floor layout and dimensions",
      },
      {
        id: "img_4",
        src: "/assets/images/Price_Trend-1774007396207.png",
        alt: "Swastik Apartment price trend chart showing property price growth in the area",
      },
    ],
    features: [
      "Prime location on NH-12 Diamond Harbour Road",
      "Near Pailan Market for daily conveniences",
      "Excellent road connectivity",
      "Developing residential locality",
      "Clear property documentation",
    ],
    highlights: [
      { id: "h1", icon: "MapIcon", label: "Project Type", value: "Apartment" },
      { id: "h2", icon: "CurrencyRupeeIcon", label: "Price", value: "Rs.3600/sqft" },
      { id: "h3", icon: "MapPinIcon", label: "Location", value: "Chak Rajmolla, WB" },
    ],
    details: [
      { label: "Property Type", value: "Residential Apartment" },
      { label: "Status", value: "New Launch" },
      { label: "Address", value: "NH-12 Diamond Harbour Road" },
      { label: "Nearest Landmark", value: "Pailan Market" },
      { label: "Locality", value: "Chak Rajmolla, WB 700104" },
      { label: "Documentation", value: "Clear & Ready" },
    ],
  };

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-20 pb-8 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link href="/projects" className="hover:text-primary transition-colors">
                Properties
              </Link>
              <Icon name="ChevronRightIcon" size={16} />
              <span className="text-foreground font-medium">{property.title}</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 mb-2">
              <span className="px-4 py-1.5 bg-secondary text-secondary-foreground rounded-full text-xs font-bold uppercase tracking-wider">
                {property.status}
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                {property.type}
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-foreground tracking-tight mb-4">
              {property.title}
            </h1>
            <p className="text-lg text-muted-foreground flex items-center gap-2">
              <Icon name="MapPinIcon" size={20} />
              {property.location}
            </p>
          </div>
        </section>

        {/* Images + Details */}
        <section className="py-12 bg-muted">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Images */}
              <div className="space-y-4">
                <div
                  className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white group cursor-pointer"
                  onClick={() => setPopupImage({ src: property.images[0].src, alt: property.images[0].alt })}
                >
                  <AppImage
                    src={property.images[0].src}
                    alt={property.images[0].alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                      <Icon name="MagnifyingGlassPlusIcon" size={32} className="text-primary" />
                    </div>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-4 py-2 bg-secondary/90 backdrop-blur-sm text-secondary-foreground rounded-full text-xs font-bold uppercase tracking-wider">
                      {property.status}
                    </span>
                  </div>
                </div>
                {property.images.length > 1 && (
                  <div className="grid grid-cols-2 gap-4">
                    {property.images.slice(1).map((img) => (
                      <div
                        key={img.id}
                        className="relative aspect-[4/3] rounded-xl overflow-hidden bg-white group cursor-pointer"
                        onClick={() => setPopupImage({ src: img.src, alt: img.alt })}
                      >
                        <AppImage
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                            <Icon name="MagnifyingGlassPlusIcon" size={24} className="text-primary" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="space-y-8">
                <div className="grid grid-cols-3 gap-4">
                  {property.highlights.map((h) => (
                    <div key={h.id} className="p-4 bg-white rounded-lg text-center">
                      <Icon name={h.icon as any} size={24} className="text-primary mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground mb-1">{h.label}</p>
                      <p className="text-sm font-bold text-foreground">{h.value}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <h2 className="font-bold text-2xl text-foreground mb-3">About This Property</h2>
                  <p className="text-muted-foreground leading-relaxed">{property.description}</p>
                </div>

                <div>
                  <h3 className="font-bold text-xl text-foreground mb-4">Key Features</h3>
                  <ul className="space-y-3">
                    {property.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <Icon name="CheckCircleIcon" size={20} className="text-primary shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link
                    href="/contact?project=swastikapartment"
                    className="flex-1 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-center hover:bg-accent transition-all hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <Icon name="ChatBubbleLeftRightIcon" size={20} />
                    Enquire About This Property
                  </Link>
                  <a
                    href="tel:+917890369000"
                    className="flex-1 px-8 py-4 bg-white border-2 border-primary text-primary rounded-lg font-bold text-center hover:bg-primary hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                    <Icon name="PhoneIcon" size={20} />
                    Call Now
                  </a>
                  <a
                    href="https://drive.google.com/uc?export=download&id=1K6t2CGNGgNr9T9sU9oZ2Q23jZNWAOKjK"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-8 py-4 bg-secondary text-secondary-foreground border-2 border-secondary rounded-lg font-bold text-center hover:bg-secondary/80 transition-all flex items-center justify-center gap-2"
                  >
                    <Icon name="ArrowDownTrayIcon" size={20} />
                    Download Brochure
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* YouTube Video */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-8">Project Video</h2>
            <div className="relative w-full max-w-sm mx-auto" style={{ aspectRatio: "9/16" }}>
              <iframe
                src="https://www.youtube.com/embed/bA77Iwq8SNE"
                title="Swastik Apartment Project Video"
                className="w-full h-full rounded-xl border border-gray-200 shadow-md"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        {/* Property Details Table */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-8">Property Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {property.details.map((d, i) => (
                <div key={i} className="flex justify-between items-center p-4 bg-muted rounded-lg">
                  <span className="text-muted-foreground font-medium">{d.label}</span>
                  <span className="font-bold text-foreground">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Price Trend */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-8">Price Trend</h2>
            <div
              className="relative rounded-2xl overflow-hidden bg-white shadow-md cursor-pointer group"
              onClick={() => setPopupImage({ src: "/assets/images/Price_Trend-1774007396207.png", alt: "Swastik Apartment price trend chart showing property price growth in the area" })}
            >
              <AppImage
                src="/assets/images/Price_Trend-1774007396207.png"
                alt="Swastik Apartment price trend chart showing property price growth in the area"
                className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                  <Icon name="MagnifyingGlassPlusIcon" size={32} className="text-primary" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary text-primary-foreground text-center">
          <div className="container mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              Interested in Swastik Apartment?
            </h2>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Contact us today to schedule a site visit and get complete details about this new launch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact?project=swastikapartment"
                className="px-8 py-4 bg-white text-primary rounded-lg font-bold hover:bg-secondary transition-all hover:scale-105"
              >
                Get in Touch
              </Link>
              <Link
                href="/projects"
                className="px-8 py-4 border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-all"
              >
                View All Properties
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyContactButtons />
      {popupImage && (
        <ImagePopup src={popupImage.src} alt={popupImage.alt} onClose={() => setPopupImage(null)} />
      )}
    </>
  );
}
